import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  CircuitBoard,
  Code2,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  FileJson,
  Plus,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { adminProjectBuilderAPI } from '../services/adminApi';
import { withBasePath } from '../utils/appPaths';
import breadboardBasicsLesson from '../features/tutorials/lessons/breadboardBasics';
import {
  decodeProjectFileContents,
  getProjectImportFallbackName,
  getProjectStoredSimulationCode,
  PROJECT_FILE_ACCEPT,
} from '../utils/projectBuilderProjectFiles';
import {
  BUILT_IN_BREADBOARD_PASSIVE,
  BUILT_IN_BUZZER_PASSIVE,
  BUILT_IN_DC_POWER_PASSIVE,
  BUILT_IN_LCD_16X2_PASSIVE,
  BUILT_IN_LED_PASSIVE,
  BUILT_IN_RESISTOR_PASSIVE,
  BUILT_IN_SEVEN_SEGMENT_DISPLAY_PASSIVE,
  BUILT_IN_SERVO_PASSIVE,
} from '../utils/projectBuilderPassives';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(300px, 0.75fr);
  gap: 18px;

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SectionDescription = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
  max-width: 760px;
`;

const StatusText = styled.p`
  margin: 0;
  color: ${props => props.$tone === 'error' ? '#b91c1c' : '#475569'};
  font-size: 0.88rem;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.span`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
`;

const Hint = styled.span`
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.45;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 11px 12px;
  font-size: 0.95rem;
  color: #0f172a;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${props => props.$code ? '220px' : props.$json ? '420px' : props.$compactJson ? '116px' : '100px'};
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px;
  font-size: ${props => (props.$code || props.$json || props.$compactJson) ? '0.88rem' : '0.95rem'};
  line-height: ${props => (props.$code || props.$json || props.$compactJson) ? '1.55' : '1.5'};
  color: #0f172a;
  background: #ffffff;
  resize: vertical;
  font-family: ${props => (props.$code || props.$json || props.$compactJson) ? "'Consolas', 'Monaco', monospace" : 'inherit'};

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  background: #f8fafc;
`;

const ToggleCopy = styled.div`
  min-width: 0;
`;

const ToggleTitle = styled.div`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
`;

const ToggleHint = styled.p`
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const ToggleTrack = styled.span`
  width: 52px;
  height: 30px;
  border-radius: 999px;
  background: ${props => props.$checked ? '#2c9fa4' : '#cbd5e1'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 3px;
  left: ${props => props.$checked ? '25px' : '3px'};
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18);
  transition: left 0.2s ease;
`;

const ToggleText = styled.span`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
`;

const UploadRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const StarterModeShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 4px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
`;

const SegmentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 8px 11px;
  border: 0;
  border-radius: 8px;
  background: ${props => props.$selected ? '#ffffff' : 'transparent'};
  color: ${props => props.$selected ? '#0f172a' : '#475569'};
  box-shadow: ${props => props.$selected ? '0 1px 4px rgba(15, 23, 42, 0.12)' : 'none'};
  font-size: 0.84rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const StarterPanel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fbfdff;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StarterPanelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PartChecklist = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PartCheckLabel = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 9px;
  align-items: start;
  border: 1px solid ${props => props.$checked ? '#99f6e4' : '#dbe3ee'};
  border-radius: 8px;
  padding: 10px;
  background: ${props => props.$checked ? '#f0fdfa' : '#ffffff'};
  cursor: pointer;
`;

const PartCheckInput = styled.input`
  margin-top: 3px;
  accent-color: #2c9fa4;
`;

const PartCheckTitle = styled.span`
  display: block;
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 900;
`;

const PartCheckMeta = styled.span`
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.35;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 14px;
  border: 1px solid ${props => props.$danger ? '#fecaca' : props.$primary ? '#2c9fa4' : '#cbd5e1'};
  border-radius: 10px;
  background: ${props => props.$danger ? '#fff1f2' : props.$primary ? '#2c9fa4' : '#ffffff'};
  color: ${props => props.$danger ? '#b91c1c' : props.$primary ? '#ffffff' : '#0f172a'};
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? '#ffe4e6' : props.$primary ? '#247f83' : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const SummaryStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  background: ${props => props.$tone === 'code' ? '#eef2ff' : '#f8fafc'};
  color: ${props => props.$tone === 'code' ? '#4338ca' : '#334155'};
  border: 1px solid ${props => props.$tone === 'code' ? '#c7d2fe' : '#dbe3ee'};
  font-size: 0.8rem;
  font-weight: 800;
`;

const StepEditorShell = styled.div`
  display: grid;
  grid-template-columns: minmax(190px, 0.35fr) minmax(0, 1fr);
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: #fbfdff;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepListButton = styled.button`
  width: 100%;
  border: 1px solid ${props => props.$selected ? '#99f6e4' : '#dbe3ee'};
  border-radius: 8px;
  padding: 10px;
  background: ${props => props.$selected ? '#f0fdfa' : '#ffffff'};
  color: #0f172a;
  text-align: left;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StepListTitle = styled.span`
  display: block;
  font-size: 0.84rem;
  font-weight: 900;
`;

const StepListMeta = styled.span`
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 0.74rem;
  line-height: 1.35;
`;

const StepEditorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const StepEditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const StepEditorTitle = styled.div`
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 900;
`;

const StepFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthField = styled(Field)`
  grid-column: 1 / -1;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 800;
`;

const CheckboxInput = styled.input`
  accent-color: #2c9fa4;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LessonCard = styled.button`
  width: 100%;
  border: 1px solid ${props => props.$selected ? '#99f6e4' : '#e5e7eb'};
  border-radius: 14px;
  padding: 14px;
  background: ${props => props.$selected ? '#f0fdfa' : '#ffffff'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #5eead4;
    background: #f8fafc;
  }
`;

const LessonTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const LessonTitle = styled.div`
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 900;
`;

const LessonMeta = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const LessonBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 999px;
  background: ${props => props.$active ? '#ecfdf5' : '#fff7ed'};
  color: ${props => props.$active ? '#047857' : '#c2410c'};
  border: 1px solid ${props => props.$active ? '#a7f3d0' : '#fed7aa'};
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const EmptyState = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  padding: 18px;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
  background: #f8fafc;
`;

const slugify = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const normalizeLineEndings = (value) => String(value || '').replace(/\r\n?/g, '\n');

const STARTER_CIRCUIT_MODE_PREBUILT = 'prebuilt';
const STARTER_CIRCUIT_MODE_MANUAL = 'manual';
const ARDUINO_UNO_BOARD_ID = 92;
const ARDUINO_UNO_BOARD_LOOKUP = {
  kind: 'arduino_uno',
  id: ARDUINO_UNO_BOARD_ID,
  slugs: [
    'arduino-uno',
    'arduino-uno-rev3',
    'arduino-uno-r3',
  ],
  names: [
    'Arduino Uno',
    'Arduino UNO',
    'Arduino Uno (Rev3)',
    'Arduino Uno Rev3',
    'Arduino Uno R3',
  ],
};

const cloneLessonValue = (value) => {
  if (value === undefined) {
    return undefined;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const MANUAL_STARTER_COMPONENT_OPTIONS = [
  {
    key: 'arduino_uno',
    label: 'Arduino UNO',
    meta: 'Resolved by Arduino UNO identity; board ID 92 is only a local fallback',
    type: 'board',
    componentId: 'lesson-arduino-uno',
    defaultLocked: true,
    buildComponent: () => ({
      id: 'lesson-arduino-uno',
      type: 'board',
      boardId: ARDUINO_UNO_BOARD_ID,
      boardSlug: 'arduino-uno',
      boardName: 'Arduino Uno (Rev3)',
      boardLookup: ARDUINO_UNO_BOARD_LOOKUP,
      board: {
        id: ARDUINO_UNO_BOARD_ID,
        slug: 'arduino-uno',
        name: 'Arduino Uno (Rev3)',
      },
      name: 'Arduino Uno (Rev3)',
      x: 30,
      y: 160,
      scale: 1,
      rotation: 0,
      targetWidth: 260,
      locked: true,
    }),
  },
  {
    key: 'breadboard',
    label: 'Breadboard',
    meta: 'Stable ID lesson-breadboard',
    componentId: 'lesson-breadboard',
    defaultLocked: true,
    definition: BUILT_IN_BREADBOARD_PASSIVE,
    x: 370,
    y: 250,
    scale: 1.45,
  },
  {
    key: 'dc_power',
    label: '5 V Source',
    meta: 'Stable ID lesson-5v-source',
    componentId: 'lesson-5v-source',
    defaultLocked: true,
    definition: BUILT_IN_DC_POWER_PASSIVE,
    x: 130,
    y: 325,
    scale: 1,
  },
  {
    key: 'led',
    label: 'LED',
    meta: 'Stable ID lesson-led',
    componentId: 'lesson-led',
    definition: BUILT_IN_LED_PASSIVE,
    x: 760,
    y: 65,
    scale: 1,
    color: '#E60000',
  },
  {
    key: 'resistor',
    label: 'Resistor',
    meta: 'Stable ID lesson-resistor',
    componentId: 'lesson-resistor',
    definition: BUILT_IN_RESISTOR_PASSIVE,
    x: 710,
    y: 190,
    scale: 1,
    selectedValue: 220,
  },
  {
    key: 'buzzer',
    label: 'Buzzer',
    meta: 'Stable ID lesson-buzzer',
    componentId: 'lesson-buzzer',
    definition: BUILT_IN_BUZZER_PASSIVE,
    x: 710,
    y: 115,
    scale: 1,
  },
  {
    key: 'servo',
    label: 'Servo',
    meta: 'Stable ID lesson-servo',
    componentId: 'lesson-servo',
    definition: BUILT_IN_SERVO_PASSIVE,
    x: 700,
    y: 260,
    scale: 1,
  },
  {
    key: 'lcd_16x2',
    label: 'LCD 16x2',
    meta: 'Stable ID lesson-lcd-16x2',
    componentId: 'lesson-lcd-16x2',
    definition: BUILT_IN_LCD_16X2_PASSIVE,
    x: 650,
    y: 350,
    scale: 1,
  },
  {
    key: 'seven_segment_display',
    label: '7-Segment Display',
    meta: 'Stable ID lesson-seven-segment',
    componentId: 'lesson-seven-segment',
    definition: BUILT_IN_SEVEN_SEGMENT_DISPLAY_PASSIVE,
    x: 790,
    y: 350,
    scale: 1,
  },
];

const DEFAULT_MANUAL_STARTER_COMPONENT_KEYS = ['arduino_uno', 'breadboard'];
const MANUAL_STARTER_COMPONENT_KEY_SET = new Set(
  MANUAL_STARTER_COMPONENT_OPTIONS.map((option) => option.key)
);

const LESSON_STEP_ACTION_OPTIONS = [
  { key: 'selectBreadboardHoles', label: 'Select holes' },
  { key: 'addComponents', label: 'Add components' },
  { key: 'moveComponents', label: 'Move components' },
  { key: 'deleteComponents', label: 'Delete components' },
  { key: 'addWires', label: 'Add wires' },
  { key: 'deleteWires', label: 'Delete wires' },
  { key: 'traceConnections', label: 'Trace connections' },
  { key: 'viewCode', label: 'View code' },
  { key: 'editCode', label: 'Edit code' },
  { key: 'runCode', label: 'Run code' },
];

const LESSON_STEP_ACTION_KEYS = new Set(
  LESSON_STEP_ACTION_OPTIONS.map((option) => option.key)
);

const stringifyStepEditorValue = (value, fallback) => JSON.stringify(
  value === undefined ? fallback : value,
  null,
  2
);

const parseStepEditorJson = (label, rawValue, fallback) => {
  const trimmedValue = String(rawValue || '').trim();

  if (!trimmedValue) {
    return cloneLessonValue(fallback);
  }

  try {
    return JSON.parse(trimmedValue);
  } catch (error) {
    throw new Error(`${label} JSON is invalid: ${error?.message || 'Unable to parse value.'}`);
  }
};

const parseStepComponentList = (value) => (
  String(value || '')
    .split(/[\n,]+/u)
    .map((componentKey) => String(componentKey || '').trim())
    .filter(Boolean)
);

const createDefaultLessonStep = (stepIndex = 0, existingStepIds = new Set()) => {
  const baseId = `step-${stepIndex + 1}`;
  let nextStepId = baseId;
  let suffix = 2;

  while (existingStepIds.has(nextStepId)) {
    nextStepId = `${baseId}-${suffix}`;
    suffix += 1;
  }

  return {
    id: nextStepId,
    title: `Step ${stepIndex + 1}`,
    instruction: 'Describe what the learner should do.',
    explanation: '',
    validation: {
      type: 'noop',
    },
    allowedActions: {
      addComponents: false,
      addWires: false,
      viewCode: false,
      editCode: false,
      runCode: false,
    },
    highlights: [],
    hints: [],
    successMessage: 'Nice work.',
  };
};

const createStepDraftFromStep = (step = createDefaultLessonStep(), stepIndex = 0) => ({
  id: String(step.id || `step-${stepIndex + 1}`),
  title: String(step.title || `Step ${stepIndex + 1}`),
  instruction: String(step.instruction || ''),
  explanation: String(step.explanation || ''),
  successMessage: String(step.successMessage || ''),
  availableComponentsText: Array.isArray(step.availableComponents)
    ? step.availableComponents.join(', ')
    : '',
  allowedActions: step.allowedActions && typeof step.allowedActions === 'object'
    ? { ...step.allowedActions }
    : {},
  validationJson: stringifyStepEditorValue(step.validation, { type: 'noop' }),
  highlightsJson: stringifyStepEditorValue(step.highlights, []),
  hintsJson: stringifyStepEditorValue(step.hints, []),
  correctionMessagesJson: stringifyStepEditorValue(step.correctionMessages, []),
});

const buildLessonStepFromDraft = (draft) => {
  const stepId = slugify(draft.id || draft.title || 'step');

  if (!stepId) {
    throw new Error('Step ID is required.');
  }

  const validation = parseStepEditorJson('Validation', draft.validationJson, { type: 'noop' });
  const highlights = parseStepEditorJson('Highlights', draft.highlightsJson, []);
  const hints = parseStepEditorJson('Hints', draft.hintsJson, []);
  const correctionMessages = parseStepEditorJson('Correction messages', draft.correctionMessagesJson, []);

  if (!validation || Array.isArray(validation) || typeof validation !== 'object') {
    throw new Error('Validation JSON must be an object.');
  }

  if (!Array.isArray(highlights)) {
    throw new Error('Highlights JSON must be an array.');
  }

  if (!Array.isArray(hints)) {
    throw new Error('Hints JSON must be an array.');
  }

  if (!Array.isArray(correctionMessages)) {
    throw new Error('Correction messages JSON must be an array.');
  }

  const allowedActions = Object.entries(draft.allowedActions || {})
    .reduce((actions, [actionKey, actionValue]) => {
      if (LESSON_STEP_ACTION_KEYS.has(actionKey)) {
        if (Boolean(actionValue)) {
          actions[actionKey] = true;
        }

        return actions;
      }

      if (actionValue !== false && actionValue !== null && actionValue !== undefined) {
        actions[actionKey] = actionValue;
      }

      return actions;
    }, {});
  const availableComponents = parseStepComponentList(draft.availableComponentsText);
  const nextStep = {
    id: stepId,
    title: String(draft.title || '').trim() || stepId,
    instruction: String(draft.instruction || '').trim(),
    explanation: String(draft.explanation || ''),
    validation,
    allowedActions,
    highlights,
    hints,
  };

  if (availableComponents.length > 0) {
    nextStep.availableComponents = availableComponents;
  }

  if (String(draft.successMessage || '').trim()) {
    nextStep.successMessage = String(draft.successMessage || '').trim();
  }

  if (correctionMessages.length > 0) {
    nextStep.correctionMessages = correctionMessages;
  }

  return nextStep;
};

const normalizeManualStarterKeys = (keys, fallbackKeys = []) => {
  const requestedKeys = new Set(
    (Array.isArray(keys) ? keys : [])
      .map((key) => String(key || '').trim())
      .filter((key) => MANUAL_STARTER_COMPONENT_KEY_SET.has(key))
  );
  const normalizedKeys = MANUAL_STARTER_COMPONENT_OPTIONS
    .filter((option) => requestedKeys.has(option.key))
    .map((option) => option.key);

  if (normalizedKeys.length > 0) {
    return normalizedKeys;
  }

  return Array.isArray(fallbackKeys) && fallbackKeys.length > 0
    ? normalizeManualStarterKeys(fallbackKeys, [])
    : [];
};

const getManualStarterKeysFromProject = (payload) => {
  const componentIds = new Set(
    (payload?.initialProject?.components || [])
      .map((component) => String(component?.id || '').trim())
      .filter(Boolean)
  );

  return MANUAL_STARTER_COMPONENT_OPTIONS
    .filter((option) => componentIds.has(option.componentId))
    .map((option) => option.key);
};

const getLessonStarterMode = (payload) => {
  const authoring = payload?.authoring && typeof payload.authoring === 'object'
    ? payload.authoring
    : {};
  const mode = String(
    authoring.starterCircuitMode ||
    payload?.starterCircuitMode ||
    ''
  ).trim();

  if ([STARTER_CIRCUIT_MODE_PREBUILT, STARTER_CIRCUIT_MODE_MANUAL].includes(mode)) {
    return mode;
  }

  return getManualStarterKeysFromProject(payload).length > 0
    ? STARTER_CIRCUIT_MODE_MANUAL
    : STARTER_CIRCUIT_MODE_PREBUILT;
};

const getManualStarterKeysFromPayload = (payload) => {
  const authoring = payload?.authoring && typeof payload.authoring === 'object'
    ? payload.authoring
    : {};

  if (Array.isArray(authoring.manualStarterComponentKeys)) {
    return normalizeManualStarterKeys(authoring.manualStarterComponentKeys, []);
  }

  const projectKeys = getManualStarterKeysFromProject(payload);

  return projectKeys.length > 0
    ? projectKeys
    : DEFAULT_MANUAL_STARTER_COMPONENT_KEYS;
};

const buildManualPassiveStarterComponent = (option) => {
  const definition = option.definition;

  if (!definition) {
    return null;
  }

  const component = {
    id: option.componentId,
    type: 'passive',
    passiveType: definition.templateType,
    passiveKey: definition.componentKey,
    passiveDefinitionId: definition.id,
    name: option.label,
    x: option.x || 0,
    y: option.y || 0,
    scale: option.scale || 1,
    rotation: option.rotation || 0,
  };

  if (option.defaultLocked) {
    component.locked = true;
  }

  if (option.selectedValue !== undefined) {
    component.selectedValue = String(option.selectedValue);
  }

  if (option.color) {
    component.color = option.color;
  }

  return component;
};

const buildManualStarterComponent = (option) => (
  typeof option.buildComponent === 'function'
    ? option.buildComponent()
    : buildManualPassiveStarterComponent(option)
);

const buildManualStarterProject = (selectedKeys, existingInitialProject = {}) => {
  const normalizedKeys = normalizeManualStarterKeys(selectedKeys, []);
  const selectedKeySet = new Set(normalizedKeys);
  const components = MANUAL_STARTER_COMPONENT_OPTIONS
    .filter((option) => selectedKeySet.has(option.key))
    .map(buildManualStarterComponent)
    .filter(Boolean);
  const canvas = {
    showGrid: false,
    snapToGrid: false,
    gridSize: 10,
    ...(existingInitialProject?.canvas || {}),
  };
  const nextProject = {
    components,
    connections: [],
    canvas,
  };

  if (existingInitialProject?.simulation && typeof existingInitialProject.simulation === 'object') {
    nextProject.simulation = cloneLessonValue(existingInitialProject.simulation);
  }

  return nextProject;
};

const buildLessonPayloadWithStarterAuthoring = (
  payload,
  starterCircuitMode,
  manualStarterComponentKeys
) => {
  const currentAuthoring = payload?.authoring && typeof payload.authoring === 'object'
    ? payload.authoring
    : {};
  const nextAuthoring = {
    ...currentAuthoring,
    starterCircuitMode,
  };

  if (starterCircuitMode === STARTER_CIRCUIT_MODE_MANUAL) {
    nextAuthoring.manualStarterComponentKeys = normalizeManualStarterKeys(
      manualStarterComponentKeys,
      []
    );
  } else {
    delete nextAuthoring.manualStarterComponentKeys;
  }

  return {
    ...payload,
    authoring: nextAuthoring,
  };
};

const buildLessonPayloadWithManualStarter = (payload, selectedKeys) => {
  const normalizedKeys = normalizeManualStarterKeys(selectedKeys, []);
  const selectedKeySet = new Set(normalizedKeys);
  const selectedOptions = MANUAL_STARTER_COMPONENT_OPTIONS
    .filter((option) => selectedKeySet.has(option.key));
  const manualAvailableComponentKeys = selectedOptions
    .map((option) => option.definition?.componentKey)
    .filter(Boolean);
  const existingAvailableComponents = Array.isArray(payload.availableComponents)
    ? payload.availableComponents.map((componentKey) => String(componentKey || '').trim()).filter(Boolean)
    : [];
  const lockedComponentIds = selectedOptions
    .filter((option) => option.defaultLocked)
    .map((option) => option.componentId);
  const nextPayload = buildLessonPayloadWithStarterAuthoring(
    payload,
    STARTER_CIRCUIT_MODE_MANUAL,
    normalizedKeys
  );

  return {
    ...nextPayload,
    availableComponents: Array.from(new Set([
      ...existingAvailableComponents,
      ...manualAvailableComponentKeys,
    ])),
    lockedComponentIds,
    initialProject: buildManualStarterProject(normalizedKeys, payload.initialProject),
  };
};

const ADMIN_LESSON_PREVIEW_STORAGE_PREFIX = 'micropinouts.adminLessonPreview.';
const ADMIN_LESSON_PREVIEW_TTL_MS = 12 * 60 * 60 * 1000;

const getAdminLessonPreviewStorageKey = (lessonId) => (
  `${ADMIN_LESSON_PREVIEW_STORAGE_PREFIX}${String(lessonId || '').trim()}`
);

const getBrowserStorage = (storageName) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[storageName] || null;
  } catch (error) {
    return null;
  }
};

const writeAdminLessonPreviewPayload = (lessonId, lessonPayload) => {
  const normalizedLessonId = String(lessonId || '').trim();

  if (!normalizedLessonId) {
    throw new Error('Lesson preview needs a lesson ID.');
  }

  const storageKey = getAdminLessonPreviewStorageKey(normalizedLessonId);
  const now = Date.now();
  const serializedPreview = JSON.stringify({
    version: 1,
    lessonPayload,
    savedAt: now,
    expiresAt: now + ADMIN_LESSON_PREVIEW_TTL_MS,
  });
  let storedLocalPreview = false;

  ['sessionStorage', 'localStorage'].forEach((storageName) => {
    const storage = getBrowserStorage(storageName);

    if (!storage) {
      return;
    }

    try {
      storage.setItem(storageKey, serializedPreview);

      if (storageName === 'localStorage') {
        storedLocalPreview = true;
      }
    } catch (error) {
      console.error(`Unable to write lesson preview to ${storageName}:`, error);
    }
  });

  if (!storedLocalPreview) {
    throw new Error('Draft preview could not be stored for the new tab. Enable browser local storage and try again.');
  }
};

const readTextFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error(`Unable to read ${file?.name || 'the selected file'}.`));
  reader.readAsText(file);
});

const createDefaultLessonPayload = () => ({
  id: 'new-lesson',
  version: 1,
  title: 'New Lesson',
  description: '',
  estimatedMinutes: 10,
  availableComponents: ['breadboard', 'dc_power', 'led', 'resistor'],
  componentLimits: {},
  lockedComponentIds: [],
  workspace: {
    fitToContentOnStart: true,
    initialZoom: 1,
  },
  initialProject: {
    components: [],
    connections: [],
    canvas: {
      showGrid: false,
      snapToGrid: false,
      gridSize: 10,
    },
  },
  steps: [
    {
      id: 'intro',
      title: 'First step',
      instruction: 'Describe what the learner should do.',
      explanation: '',
      validation: {
        type: 'noop',
      },
      allowedActions: {
        addComponents: false,
        addWires: false,
        viewCode: false,
        editCode: false,
        runCode: false,
      },
      highlights: [],
      hints: [],
      successMessage: 'Nice work.',
    },
  ],
  completion: {
    title: 'Lesson complete',
    message: 'Nice work. You completed the lesson.',
  },
});

const parseLessonJson = (lessonJson) => {
  const parsed = JSON.parse(String(lessonJson || '').trim());

  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    throw new Error('Lesson JSON must be an object.');
  }

  if (!Array.isArray(parsed.steps) || parsed.steps.length === 0) {
    throw new Error('Lesson JSON must include at least one step.');
  }

  return parsed;
};

const stringifyLessonPayload = (payload) => JSON.stringify(payload || createDefaultLessonPayload(), null, 2);

const buildBuilderLessonUrl = (lessonId, { embed = false, preview = false } = {}) => {
  const normalizedLessonId = String(lessonId || '').trim();

  if (!normalizedLessonId) {
    return '';
  }

  const searchParams = new URLSearchParams({
    mode: 'lesson',
    lesson: normalizedLessonId,
  });

  if (embed) {
    searchParams.set('embed', '1');
  }

  if (preview) {
    searchParams.set('preview', 'admin-draft');
    searchParams.set('restart', '1');
  }

  const path = withBasePath(`/builder/?${searchParams.toString()}`);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return `${origin}${path}`;
};

const getLessonStoredSimulationCode = (lessonPayload) => (
  normalizeLineEndings(
    lessonPayload?.initialProject?.simulation?.code ||
    lessonPayload?.simulation?.code ||
    lessonPayload?.simulationCode ||
    ''
  )
);

const normalizeLessonRecord = (record) => {
  const lessonPayload = record?.lesson_payload ?? record?.lessonPayload ?? {};
  const recordId = record?.record_id ?? record?.recordId ?? record?.database_id ?? null;
  const lessonId = String(record?.lesson_id ?? record?.lessonId ?? lessonPayload?.id ?? '');
  const stepCount = Number(record?.step_count ?? record?.stepCount ?? lessonPayload?.steps?.length ?? 0);
  const componentCount = Number(
    record?.component_count ??
    record?.componentCount ??
    lessonPayload?.initialProject?.components?.length ??
    0
  );
  const hasSimulationCode = record?.has_simulation_code ?? record?.hasSimulationCode;
  const isPublished = record?.is_published ?? record?.isPublished;

  return {
    recordId,
    lessonId,
    title: String(record?.title ?? lessonPayload?.title ?? ''),
    description: String(record?.description ?? lessonPayload?.description ?? ''),
    version: Number(record?.version ?? lessonPayload?.version ?? 1) || 1,
    estimatedMinutes: record?.estimated_minutes ?? record?.estimatedMinutes ?? lessonPayload?.estimatedMinutes ?? '',
    sourceFileName: String(record?.source_file_name ?? record?.sourceFileName ?? ''),
    stepCount: Number.isFinite(stepCount) ? stepCount : 0,
    componentCount: Number.isFinite(componentCount) ? componentCount : 0,
    hasSimulationCode: hasSimulationCode === true || hasSimulationCode === 1 || hasSimulationCode === '1',
    isPublished: isPublished === true || isPublished === 1 || isPublished === '1',
    publishedAt: record?.published_at ?? record?.publishedAt ?? '',
    updatedAt: record?.updated_at ?? record?.updatedAt ?? '',
    lessonPayload,
    simulationCode: String(record?.simulation_code ?? record?.simulationCode ?? getLessonStoredSimulationCode(lessonPayload)),
  };
};

const createFormStateFromPayload = (payload = createDefaultLessonPayload()) => {
  const lessonId = slugify(payload.id || payload.title || 'new-lesson') || 'new-lesson';
  const title = String(payload.title || 'New Lesson');

  return {
    recordId: null,
    lessonId,
    title,
    description: String(payload.description || ''),
    version: Number(payload.version || 1),
    estimatedMinutes: payload.estimatedMinutes ?? '',
    sourceFileName: '',
    lessonJson: stringifyLessonPayload({
      ...payload,
      id: lessonId,
      title,
    }),
    simulationCode: getLessonStoredSimulationCode(payload),
    starterMode: getLessonStarterMode(payload),
    manualStarterKeys: getManualStarterKeysFromPayload(payload),
    isPublished: false,
  };
};

const createEmptyFormState = () => createFormStateFromPayload(createDefaultLessonPayload());

const ProjectBuilderLessonsManager = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [form, setForm] = useState(createEmptyFormState());
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [stepDraft, setStepDraft] = useState(() => createStepDraftFromStep(createDefaultLessonPayload().steps[0], 0));
  const [stepEditorError, setStepEditorError] = useState('');
  const projectFileInputRef = useRef(null);
  const lessonJsonFileInputRef = useRef(null);
  const codeFileInputRef = useRef(null);

  const selectedLesson = useMemo(
    () => lessons.find((lesson) => String(lesson.recordId) === String(selectedRecordId)) || null,
    [lessons, selectedRecordId]
  );

  const parsedLessonSummary = useMemo(() => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);

      return {
        valid: true,
        lessonPayload,
        stepCount: Array.isArray(lessonPayload.steps) ? lessonPayload.steps.length : 0,
        componentCount: Array.isArray(lessonPayload.initialProject?.components)
          ? lessonPayload.initialProject.components.length
          : 0,
        connectionCount: Array.isArray(lessonPayload.initialProject?.connections)
          ? lessonPayload.initialProject.connections.length
          : 0,
        error: '',
      };
    } catch (parseError) {
      return {
        valid: false,
        lessonPayload: null,
        stepCount: 0,
        componentCount: 0,
        connectionCount: 0,
        error: parseError?.message || 'Lesson JSON could not be parsed.',
      };
    }
  }, [form.lessonJson]);

  const lessonUrl = useMemo(() => {
    return buildBuilderLessonUrl(form.lessonId);
  }, [form.lessonId]);

  const iframeUrl = useMemo(() => (
    buildBuilderLessonUrl(form.lessonId, { embed: true })
  ), [form.lessonId]);

  const manualStarterKeySet = useMemo(
    () => new Set(normalizeManualStarterKeys(form.manualStarterKeys, [])),
    [form.manualStarterKeys]
  );

  const selectedManualStarterLabels = useMemo(() => (
    MANUAL_STARTER_COMPONENT_OPTIONS
      .filter((option) => manualStarterKeySet.has(option.key))
      .map((option) => option.label)
  ), [manualStarterKeySet]);
  const lessonSteps = useMemo(() => (
    parsedLessonSummary.valid && Array.isArray(parsedLessonSummary.lessonPayload?.steps)
      ? parsedLessonSummary.lessonPayload.steps
      : []
  ), [parsedLessonSummary]);

  useEffect(() => {
    if (!parsedLessonSummary.valid || lessonSteps.length === 0) {
      return;
    }

    const nextStepIndex = Math.min(selectedStepIndex, lessonSteps.length - 1);

    if (nextStepIndex !== selectedStepIndex) {
      setSelectedStepIndex(nextStepIndex);
      return;
    }

    setStepDraft(createStepDraftFromStep(lessonSteps[nextStepIndex], nextStepIndex));
    setStepEditorError('');
  }, [lessonSteps, parsedLessonSummary.valid, selectedStepIndex]);

  useEffect(() => {
    let isMounted = true;

    const loadLessons = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await adminProjectBuilderAPI.getLessons();
        if (!isMounted) {
          return;
        }

        const nextLessons = Array.isArray(response?.data)
          ? response.data.map(normalizeLessonRecord)
          : [];

        setLessons(nextLessons);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(
          loadError?.response?.data?.error ||
          loadError?.response?.data?.message ||
          loadError?.message ||
          'Failed to load Builder lessons.'
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLessons();

    return () => {
      isMounted = false;
    };
  }, []);

  const resetForm = () => {
    setSelectedRecordId(null);
    setForm(createEmptyFormState());
    setSelectedStepIndex(0);
    setStatus('');
    setError('');
    setStepEditorError('');
  };

  const loadLessonIntoForm = (lesson) => {
    if (!lesson) {
      return;
    }

    setSelectedRecordId(lesson.recordId);
    setForm({
      recordId: lesson.recordId,
      lessonId: lesson.lessonId,
      title: lesson.title,
      description: lesson.description,
      version: lesson.version,
      estimatedMinutes: lesson.estimatedMinutes ?? '',
      sourceFileName: lesson.sourceFileName,
      lessonJson: stringifyLessonPayload(lesson.lessonPayload),
      simulationCode: lesson.simulationCode,
      starterMode: getLessonStarterMode(lesson.lessonPayload),
      manualStarterKeys: getManualStarterKeysFromPayload(lesson.lessonPayload),
      isPublished: lesson.isPublished,
    });
    setSelectedStepIndex(0);
    setStatus('');
    setError('');
    setStepEditorError('');
  };

  const updateMetadata = (updates) => {
    setForm((currentForm) => ({
      ...currentForm,
      ...updates,
    }));
  };

  const updateStepDraft = (updates) => {
    setStepDraft((currentDraft) => ({
      ...currentDraft,
      ...updates,
    }));
  };

  const updateStepAllowedAction = (actionKey, checked) => {
    setStepDraft((currentDraft) => ({
      ...currentDraft,
      allowedActions: {
        ...(currentDraft.allowedActions || {}),
        [actionKey]: checked,
      },
    }));
  };

  const selectStepForEditing = (stepIndex) => {
    setSelectedStepIndex(stepIndex);
    setStepEditorError('');
  };

  const addLessonStep = () => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const currentSteps = Array.isArray(lessonPayload.steps) ? lessonPayload.steps : [];
      const existingStepIds = new Set(
        currentSteps.map((step) => String(step?.id || '')).filter(Boolean)
      );
      const nextStep = createDefaultLessonStep(currentSteps.length, existingStepIds);
      const nextPayload = {
        ...lessonPayload,
        steps: [
          ...currentSteps,
          nextStep,
        ],
      };

      setForm((currentForm) => ({
        ...currentForm,
        lessonJson: stringifyLessonPayload(nextPayload),
      }));
      setSelectedStepIndex(currentSteps.length);
      setStepDraft(createStepDraftFromStep(nextStep, currentSteps.length));
      setStepEditorError('');
      setStatus('Added a new lesson step.');
      setError('');
    } catch (stepError) {
      setStepEditorError(stepError?.message || 'Lesson JSON could not be parsed.');
    }
  };

  const saveSelectedLessonStep = () => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const currentSteps = Array.isArray(lessonPayload.steps) ? lessonPayload.steps : [];

      if (currentSteps.length === 0) {
        throw new Error('Lesson JSON must include at least one step.');
      }

      const nextStep = buildLessonStepFromDraft(stepDraft);
      const duplicateStepIndex = currentSteps.findIndex((step, stepIndex) => (
        stepIndex !== selectedStepIndex &&
        String(step?.id || '') === nextStep.id
      ));

      if (duplicateStepIndex >= 0) {
        throw new Error(`Step ID "${nextStep.id}" is already used.`);
      }

      const nextSteps = currentSteps.map((step, stepIndex) => (
        stepIndex === selectedStepIndex ? nextStep : step
      ));
      const nextPayload = {
        ...lessonPayload,
        steps: nextSteps,
      };

      setForm((currentForm) => ({
        ...currentForm,
        lessonJson: stringifyLessonPayload(nextPayload),
      }));
      setStepDraft(createStepDraftFromStep(nextStep, selectedStepIndex));
      setStepEditorError('');
      setStatus(`Saved step "${nextStep.title}".`);
      setError('');
    } catch (stepError) {
      setStepEditorError(stepError?.message || 'Step could not be saved.');
    }
  };

  const deleteSelectedLessonStep = () => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const currentSteps = Array.isArray(lessonPayload.steps) ? lessonPayload.steps : [];

      if (currentSteps.length <= 1) {
        throw new Error('A lesson needs at least one step.');
      }

      const deletedStep = currentSteps[selectedStepIndex];
      const nextSteps = currentSteps.filter((step, stepIndex) => stepIndex !== selectedStepIndex);
      const nextStepIndex = Math.max(0, Math.min(selectedStepIndex, nextSteps.length - 1));
      const nextPayload = {
        ...lessonPayload,
        steps: nextSteps,
      };

      setForm((currentForm) => ({
        ...currentForm,
        lessonJson: stringifyLessonPayload(nextPayload),
      }));
      setSelectedStepIndex(nextStepIndex);
      setStepDraft(createStepDraftFromStep(nextSteps[nextStepIndex], nextStepIndex));
      setStepEditorError('');
      setStatus(`Deleted step "${deletedStep?.title || deletedStep?.id || selectedStepIndex + 1}".`);
      setError('');
    } catch (stepError) {
      setStepEditorError(stepError?.message || 'Step could not be deleted.');
    }
  };

  const moveSelectedLessonStep = (direction) => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const currentSteps = Array.isArray(lessonPayload.steps) ? lessonPayload.steps : [];
      const targetIndex = selectedStepIndex + direction;

      if (targetIndex < 0 || targetIndex >= currentSteps.length) {
        return;
      }

      const nextSteps = [...currentSteps];
      const [selectedStep] = nextSteps.splice(selectedStepIndex, 1);
      nextSteps.splice(targetIndex, 0, selectedStep);

      setForm((currentForm) => ({
        ...currentForm,
        lessonJson: stringifyLessonPayload({
          ...lessonPayload,
          steps: nextSteps,
        }),
      }));
      setSelectedStepIndex(targetIndex);
      setStepDraft(createStepDraftFromStep(selectedStep, targetIndex));
      setStepEditorError('');
      setStatus('Moved lesson step.');
      setError('');
    } catch (stepError) {
      setStepEditorError(stepError?.message || 'Step could not be moved.');
    }
  };

  const applyManualStarterSelection = (selectedKeys) => {
    try {
      const normalizedKeys = normalizeManualStarterKeys(selectedKeys, []);
      const lessonPayload = parseLessonJson(form.lessonJson);
      const nextLessonPayload = buildLessonPayloadWithManualStarter(lessonPayload, normalizedKeys);

      setForm((currentForm) => ({
        ...currentForm,
        starterMode: STARTER_CIRCUIT_MODE_MANUAL,
        manualStarterKeys: normalizedKeys,
        lessonJson: stringifyLessonPayload(nextLessonPayload),
      }));
      setStatus('Manual starter circuit updated.');
      setError('');
    } catch (starterError) {
      setStatus('');
      setError(starterError?.message || 'Lesson JSON could not be parsed.');
    }
  };

  const changeStarterCircuitMode = (mode) => {
    if (mode === STARTER_CIRCUIT_MODE_MANUAL) {
      const selectedKeys = Array.isArray(form.manualStarterKeys)
        ? normalizeManualStarterKeys(form.manualStarterKeys, [])
        : DEFAULT_MANUAL_STARTER_COMPONENT_KEYS;

      applyManualStarterSelection(selectedKeys);
      return;
    }

    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const nextLessonPayload = buildLessonPayloadWithStarterAuthoring(
        lessonPayload,
        STARTER_CIRCUIT_MODE_PREBUILT,
        []
      );

      setForm((currentForm) => ({
        ...currentForm,
        starterMode: STARTER_CIRCUIT_MODE_PREBUILT,
        lessonJson: stringifyLessonPayload(nextLessonPayload),
      }));
      setStatus('Prebuilt starter mode selected. Import a .mpbproj or .mbproj file to replace the starter circuit.');
      setError('');
    } catch (starterError) {
      setStatus('');
      setError(starterError?.message || 'Lesson JSON could not be parsed.');
    }
  };

  const toggleManualStarterComponent = (componentKey) => {
    const currentKeys = normalizeManualStarterKeys(form.manualStarterKeys, []);
    const currentKeySet = new Set(currentKeys);

    if (currentKeySet.has(componentKey)) {
      currentKeySet.delete(componentKey);
    } else {
      currentKeySet.add(componentKey);
    }

    applyManualStarterSelection(Array.from(currentKeySet));
  };

  const syncMetadataIntoJson = () => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const nextLessonId = slugify(form.lessonId || form.title || lessonPayload.id) || 'new-lesson';
      const nextPayload = {
        ...lessonPayload,
        id: nextLessonId,
        title: form.title || lessonPayload.title || 'New Lesson',
        version: Number(form.version || lessonPayload.version || 1),
      };

      if (String(form.description || '').trim()) {
        nextPayload.description = form.description;
      } else {
        delete nextPayload.description;
      }

      if (form.estimatedMinutes !== '' && form.estimatedMinutes !== null) {
        nextPayload.estimatedMinutes = Number(form.estimatedMinutes);
      } else {
        delete nextPayload.estimatedMinutes;
      }

      setForm((currentForm) => ({
        ...currentForm,
        lessonId: nextLessonId,
        lessonJson: stringifyLessonPayload(nextPayload),
      }));
      setStatus('Metadata synced into lesson JSON.');
      setError('');
    } catch (syncError) {
      setStatus('');
      setError(syncError?.message || 'Lesson JSON could not be parsed.');
    }
  };

  const handleLessonJsonUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      setError('');
      setStatus(`Reading ${file.name}...`);

      const rawContents = await readTextFile(file);
      const lessonPayload = parseLessonJson(rawContents);
      const nextLessonId = slugify(lessonPayload.id || lessonPayload.title || getProjectImportFallbackName(file.name)) || 'new-lesson';

      setForm((currentForm) => ({
        ...currentForm,
        lessonId: nextLessonId,
        title: String(lessonPayload.title || currentForm.title || 'New Lesson'),
        description: String(lessonPayload.description || ''),
        version: Number(lessonPayload.version || 1),
        estimatedMinutes: lessonPayload.estimatedMinutes ?? '',
        sourceFileName: file.name,
        lessonJson: stringifyLessonPayload({
          ...lessonPayload,
          id: nextLessonId,
        }),
        simulationCode: getLessonStoredSimulationCode(lessonPayload),
        starterMode: getLessonStarterMode(lessonPayload),
        manualStarterKeys: getManualStarterKeysFromPayload(lessonPayload),
      }));
      setSelectedStepIndex(0);
      setStepEditorError('');
      setStatus(`Loaded ${file.name}. Review and save the lesson.`);
    } catch (uploadError) {
      setStatus('');
      setError(uploadError?.message || 'Failed to read the selected lesson JSON file.');
    }
  };

  const loadBuiltInBreadboardLesson = () => {
    const lessonPayload = JSON.parse(JSON.stringify(breadboardBasicsLesson));
    const nextFormState = createFormStateFromPayload(lessonPayload);

    setSelectedRecordId(null);
    setForm({
      ...nextFormState,
      sourceFileName: 'breadboardBasics.js',
      isPublished: false,
    });
    setSelectedStepIndex(0);
    setStepEditorError('');
    setError('');
    setStatus('Loaded the built-in breadboard lesson as a new editable draft.');
  };

  const handleProjectFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      setError('');
      setStatus(`Reading ${file.name}...`);

      const rawContents = await readTextFile(file);
      const projectPayload = decodeProjectFileContents(rawContents);

      if (!projectPayload || !Array.isArray(projectPayload.components) || !Array.isArray(projectPayload.connections)) {
        throw new Error('That file is not a valid Builder project.');
      }

      const currentLessonPayload = parseLessonJson(form.lessonJson);
      const projectSimulationCode = getProjectStoredSimulationCode(projectPayload);
      const initialProject = {
        components: projectPayload.components,
        connections: projectPayload.connections,
        canvas: projectPayload.canvas || {},
      };

      if (projectPayload.simulation && typeof projectPayload.simulation === 'object') {
        initialProject.simulation = { ...projectPayload.simulation };
      }

      const nextLessonPayload = {
        ...buildLessonPayloadWithStarterAuthoring(
          currentLessonPayload,
          STARTER_CIRCUIT_MODE_PREBUILT,
          []
        ),
        initialProject,
      };

      setForm((currentForm) => ({
        ...currentForm,
        title: currentForm.title.trim()
          ? currentForm.title
          : (projectPayload.name || getProjectImportFallbackName(file.name)),
        sourceFileName: file.name,
        lessonJson: stringifyLessonPayload(nextLessonPayload),
        simulationCode: currentForm.simulationCode.trim()
          ? currentForm.simulationCode
          : projectSimulationCode,
        starterMode: STARTER_CIRCUIT_MODE_PREBUILT,
      }));
      setStatus(`Loaded ${file.name} as the initial lesson circuit.`);
    } catch (uploadError) {
      setStatus('');
      setError(uploadError?.message || 'Failed to read the selected project file.');
    }
  };

  const handleCodeFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      setError('');
      const rawContents = await readTextFile(file);

      setForm((currentForm) => ({
        ...currentForm,
        simulationCode: normalizeLineEndings(rawContents),
      }));
      setStatus(`Loaded code from ${file.name}.`);
    } catch (uploadError) {
      setStatus('');
      setError(uploadError?.message || 'Failed to read the selected code file.');
    }
  };

  const buildSavePayload = () => {
    const lessonPayload = parseLessonJson(form.lessonJson);
    const lessonId = slugify(form.lessonId || form.title || lessonPayload.id) || 'new-lesson';
    const version = Number(form.version || lessonPayload.version || 1);
    const estimatedMinutes = form.estimatedMinutes === '' || form.estimatedMinutes === null
      ? null
      : Number(form.estimatedMinutes);
    const nextLessonPayload = {
      ...lessonPayload,
      id: lessonId,
      title: form.title || lessonPayload.title || 'New Lesson',
      version,
      initialProject: {
        components: [],
        connections: [],
        canvas: {},
        ...(lessonPayload.initialProject || {}),
      },
    };

    if (String(form.description || '').trim()) {
      nextLessonPayload.description = form.description;
    } else {
      delete nextLessonPayload.description;
    }

    if (estimatedMinutes !== null && Number.isFinite(estimatedMinutes)) {
      nextLessonPayload.estimatedMinutes = estimatedMinutes;
    } else {
      delete nextLessonPayload.estimatedMinutes;
    }

    if (!Array.isArray(nextLessonPayload.initialProject.components)) {
      nextLessonPayload.initialProject.components = [];
    }

    if (!Array.isArray(nextLessonPayload.initialProject.connections)) {
      nextLessonPayload.initialProject.connections = [];
    }

    const formSimulationCode = normalizeLineEndings(form.simulationCode);
    const jsonSimulationCode = getLessonStoredSimulationCode(lessonPayload);
    const simulationCode = formSimulationCode.trim()
      ? formSimulationCode
      : jsonSimulationCode;

    if (simulationCode.trim()) {
      nextLessonPayload.initialProject.simulation = {
        ...(nextLessonPayload.initialProject.simulation || {}),
        code: simulationCode,
      };
    } else if (nextLessonPayload.initialProject.simulation) {
      delete nextLessonPayload.initialProject.simulation.code;

      if (Object.keys(nextLessonPayload.initialProject.simulation).length === 0) {
        delete nextLessonPayload.initialProject.simulation;
      }
    }

    return {
      lesson_id: lessonId,
      title: nextLessonPayload.title,
      description: form.description,
      source_file_name: form.sourceFileName,
      lesson_payload: nextLessonPayload,
      version,
      estimated_minutes: estimatedMinutes,
      simulation_code: simulationCode,
      is_published: form.isPublished,
    };
  };

  const handleSave = async () => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setStatus(form.recordId ? 'Saving lesson...' : 'Creating lesson...');

      const payload = buildSavePayload();
      const response = form.recordId
        ? await adminProjectBuilderAPI.updateLesson(form.recordId, payload)
        : await adminProjectBuilderAPI.createLesson(payload);
      const savedLesson = normalizeLessonRecord(response?.data?.data || response?.data);

      setLessons((currentLessons) => {
        const nextLessons = [
          savedLesson,
          ...currentLessons.filter((lesson) => String(lesson.recordId) !== String(savedLesson.recordId)),
        ];

        return nextLessons.sort((left, right) => {
          const rightDate = String(right.updatedAt || '');
          const leftDate = String(left.updatedAt || '');
          return rightDate.localeCompare(leftDate);
        });
      });

      loadLessonIntoForm(savedLesson);
      setStatus(form.recordId ? 'Lesson updated.' : 'Lesson created.');
    } catch (saveError) {
      setStatus('');
      setError(
        saveError?.response?.data?.error ||
        saveError?.response?.data?.message ||
        saveError?.message ||
        'Failed to save the Builder lesson.'
      );
    } finally {
      setSaving(false);
    }
  };

  const duplicateCurrentLesson = () => {
    try {
      const lessonPayload = parseLessonJson(form.lessonJson);
      const baseLessonId = slugify(form.lessonId || lessonPayload.id || 'lesson') || 'lesson';
      const baseCopyId = slugify(`${baseLessonId}-copy`) || 'lesson-copy';
      const existingLessonIds = new Set(
        lessons
          .map((lesson) => String(lesson.lessonId || '').trim())
          .filter(Boolean)
      );
      let nextLessonId = baseCopyId;
      let suffix = 2;

      while (existingLessonIds.has(nextLessonId)) {
        nextLessonId = `${baseCopyId}-${suffix}`;
        suffix += 1;
      }

      const nextTitle = `${form.title || lessonPayload.title || 'Lesson'} Copy`;
      const nextPayload = {
        ...lessonPayload,
        id: nextLessonId,
        title: nextTitle,
      };

      setSelectedRecordId(null);
      setForm({
        ...form,
        recordId: null,
        lessonId: nextLessonId,
        title: nextTitle,
        sourceFileName: '',
        isPublished: false,
        lessonJson: stringifyLessonPayload(nextPayload),
      });
      setError('');
      setStatus('Duplicated as a new draft. Save it when you are ready.');
    } catch (duplicateError) {
      setStatus('');
      setError(duplicateError?.message || 'Lesson JSON could not be duplicated.');
    }
  };

  const handleDelete = async () => {
    if (!form.recordId || saving) {
      return;
    }

    const shouldDelete = window.confirm(`Delete "${form.title || 'this lesson'}"?`);

    if (!shouldDelete) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setStatus('Deleting lesson...');

      await adminProjectBuilderAPI.deleteLesson(form.recordId);
      setLessons((currentLessons) => currentLessons.filter((lesson) => String(lesson.recordId) !== String(form.recordId)));
      resetForm();
      setStatus('Lesson deleted.');
    } catch (deleteError) {
      setStatus('');
      setError(
        deleteError?.response?.data?.error ||
        deleteError?.response?.data?.message ||
        deleteError?.message ||
        'Failed to delete the Builder lesson.'
      );
    } finally {
      setSaving(false);
    }
  };

  const copyIframeUrl = async () => {
    if (!iframeUrl || !form.isPublished) {
      setError('Publish the lesson before copying its public iframe URL.');
      return;
    }

    try {
      await navigator.clipboard.writeText(iframeUrl);
      setStatus('Iframe URL copied.');
    } catch (copyError) {
      setStatus(iframeUrl);
    }
  };

  const openLessonPreview = (embed = false) => {
    try {
      const payload = buildSavePayload();
      const targetUrl = buildBuilderLessonUrl(payload.lesson_id, {
        embed,
        preview: true,
      });

      if (!targetUrl) {
        return;
      }

      writeAdminLessonPreviewPayload(payload.lesson_id, payload.lesson_payload);

      if (typeof window !== 'undefined') {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }

      setError('');
      setStatus(embed ? 'Opened draft iframe preview.' : 'Opened draft lesson preview.');
    } catch (previewError) {
      setStatus('');
      setError(previewError?.message || 'Lesson preview could not be opened.');
    }
  };

  const openPublishedLesson = (embed = false) => {
    const targetUrl = embed ? iframeUrl : lessonUrl;

    if (!targetUrl || !form.isPublished) {
      return;
    }

    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Layout>
      <Card>
        <SectionHeader>
          <div>
            <SectionTitle>
              <BookOpen size={18} />
              Lesson Editor
            </SectionTitle>
            <SectionDescription>
              Save guided lesson definitions that load in Project Builder. This first editor works directly with the lesson JSON used by the runtime.
            </SectionDescription>
          </div>

          <Button type="button" onClick={resetForm} disabled={saving}>
            <Plus size={16} />
            New Lesson
          </Button>
        </SectionHeader>

        <Grid>
          <Field>
            <FieldLabel>Lesson title</FieldLabel>
            <Input
              type="text"
              value={form.title}
              onChange={(event) => updateMetadata({
                title: event.target.value,
                lessonId: form.lessonId || slugify(event.target.value),
              })}
              placeholder="Arduino LED Blink"
            />
          </Field>

          <Field>
            <FieldLabel>Lesson ID</FieldLabel>
            <Input
              type="text"
              value={form.lessonId}
              onChange={(event) => updateMetadata({ lessonId: slugify(event.target.value) })}
              placeholder="arduino-led-blink"
            />
          </Field>

          <Field>
            <FieldLabel>Version</FieldLabel>
            <Input
              type="number"
              min="1"
              value={form.version}
              onChange={(event) => updateMetadata({ version: event.target.value })}
            />
          </Field>

          <Field>
            <FieldLabel>Estimated minutes</FieldLabel>
            <Input
              type="number"
              min="0"
              value={form.estimatedMinutes}
              onChange={(event) => updateMetadata({ estimatedMinutes: event.target.value })}
              placeholder="12"
            />
          </Field>
        </Grid>

        <Field>
          <FieldLabel>Description</FieldLabel>
          <Textarea
            value={form.description}
            onChange={(event) => updateMetadata({ description: event.target.value })}
            placeholder="Short description shown around the lesson."
          />
        </Field>

        <Field>
          <FieldLabel>Starter circuit</FieldLabel>
          <Hint>
            Choose whether the lesson starts from an exported Builder project or from a manual set of stable starter parts.
          </Hint>

          <StarterModeShell>
            <SegmentedControl aria-label="Starter circuit mode">
              <SegmentButton
                type="button"
                $selected={form.starterMode === STARTER_CIRCUIT_MODE_PREBUILT}
                onClick={() => changeStarterCircuitMode(STARTER_CIRCUIT_MODE_PREBUILT)}
                disabled={saving}
              >
                <Upload size={15} />
                Prebuilt circuit
              </SegmentButton>
              <SegmentButton
                type="button"
                $selected={form.starterMode === STARTER_CIRCUIT_MODE_MANUAL}
                onClick={() => changeStarterCircuitMode(STARTER_CIRCUIT_MODE_MANUAL)}
                disabled={saving}
              >
                <CircuitBoard size={15} />
                Manual parts
              </SegmentButton>
            </SegmentedControl>

            {form.starterMode === STARTER_CIRCUIT_MODE_MANUAL ? (
              <StarterPanel>
                <StarterPanelRow>
                  <Hint>
                    {selectedManualStarterLabels.length > 0
                      ? `Starts with: ${selectedManualStarterLabels.join(', ')}.`
                      : 'Starts with an empty canvas.'}
                  </Hint>
                </StarterPanelRow>
                <PartChecklist>
                  {MANUAL_STARTER_COMPONENT_OPTIONS.map((option) => {
                    const checked = manualStarterKeySet.has(option.key);

                    return (
                      <PartCheckLabel key={option.key} $checked={checked}>
                        <PartCheckInput
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleManualStarterComponent(option.key)}
                          disabled={saving}
                        />
                        <span>
                          <PartCheckTitle>{option.label}</PartCheckTitle>
                          <PartCheckMeta>{option.meta}</PartCheckMeta>
                        </span>
                      </PartCheckLabel>
                    );
                  })}
                </PartChecklist>
              </StarterPanel>
            ) : (
              <StarterPanel>
                <StarterPanelRow>
                  <Hint>
                    Import a Project Builder export when the starter circuit should include exact wiring, placements, and generated component IDs.
                  </Hint>
                  <Button
                    type="button"
                    onClick={() => projectFileInputRef.current?.click()}
                    disabled={saving}
                  >
                    <Upload size={16} />
                    Choose .mpbproj / .mbproj
                  </Button>
                </StarterPanelRow>
              </StarterPanel>
            )}

            <HiddenFileInput
              ref={projectFileInputRef}
              type="file"
              accept={PROJECT_FILE_ACCEPT}
              onChange={handleProjectFileUpload}
            />
          </StarterModeShell>
        </Field>

        <Field>
          <FieldLabel>Uploads</FieldLabel>
          <Hint>
            Upload lesson JSON to edit an existing definition, or upload Arduino code to attach a starter sketch.
          </Hint>

          <UploadRow>
            <Button
              type="button"
              onClick={() => lessonJsonFileInputRef.current?.click()}
              disabled={saving}
            >
              <FileJson size={16} />
              Upload Lesson JSON
            </Button>

            <Button
              type="button"
              onClick={() => codeFileInputRef.current?.click()}
              disabled={saving}
            >
              <Code2 size={16} />
              Upload Code
            </Button>

            <Button
              type="button"
              onClick={loadBuiltInBreadboardLesson}
              disabled={saving}
            >
              <BookOpen size={16} />
              Load Breadboard Basics
            </Button>

            <HiddenFileInput
              ref={lessonJsonFileInputRef}
              type="file"
              accept=".json,application/json,text/plain"
              onChange={handleLessonJsonUpload}
            />

            <HiddenFileInput
              ref={codeFileInputRef}
              type="file"
              accept=".ino,.txt,.cpp,.c,.h,.hpp,text/plain"
              onChange={handleCodeFileUpload}
            />
          </UploadRow>
        </Field>

        <SummaryStrip>
          <SummaryBadge>
            <BookOpen size={14} />
            {parsedLessonSummary.valid ? parsedLessonSummary.stepCount : 0} step{parsedLessonSummary.stepCount === 1 ? '' : 's'}
          </SummaryBadge>
          <SummaryBadge>
            <CircuitBoard size={14} />
            {parsedLessonSummary.valid ? parsedLessonSummary.componentCount : 0} initial part{parsedLessonSummary.componentCount === 1 ? '' : 's'}
          </SummaryBadge>
          <SummaryBadge>
            <CircuitBoard size={14} />
            {parsedLessonSummary.valid ? parsedLessonSummary.connectionCount : 0} wire{parsedLessonSummary.connectionCount === 1 ? '' : 's'}
          </SummaryBadge>
          <SummaryBadge $tone="code">
            <Code2 size={14} />
            {form.simulationCode.trim() ? 'Code attached' : 'No code attached'}
          </SummaryBadge>
        </SummaryStrip>

        <Field>
          <FieldLabel>Steps</FieldLabel>
          <Hint>
            Edit one step at a time. Save Step updates the lesson JSON below; Save Changes stores the lesson.
          </Hint>

          {parsedLessonSummary.valid ? (
            <StepEditorShell>
              <StepList>
                <Button
                  type="button"
                  onClick={addLessonStep}
                  disabled={saving}
                >
                  <Plus size={16} />
                  Add Step
                </Button>

                {lessonSteps.map((step, stepIndex) => (
                  <StepListButton
                    key={`${step?.id || 'step'}-${stepIndex}`}
                    type="button"
                    $selected={stepIndex === selectedStepIndex}
                    onClick={() => selectStepForEditing(stepIndex)}
                    disabled={saving}
                  >
                    <StepListTitle>
                      {stepIndex + 1}. {step?.title || step?.id || 'Untitled step'}
                    </StepListTitle>
                    <StepListMeta>{step?.id || `step-${stepIndex + 1}`}</StepListMeta>
                  </StepListButton>
                ))}
              </StepList>

              <StepEditorPanel>
                <StepEditorHeader>
                  <StepEditorTitle>
                    Edit step {selectedStepIndex + 1}
                  </StepEditorTitle>
                  <ActionRow>
                    <Button
                      type="button"
                      onClick={() => moveSelectedLessonStep(-1)}
                      disabled={saving || selectedStepIndex <= 0}
                    >
                      <ArrowUp size={16} />
                      Up
                    </Button>
                    <Button
                      type="button"
                      onClick={() => moveSelectedLessonStep(1)}
                      disabled={saving || selectedStepIndex >= lessonSteps.length - 1}
                    >
                      <ArrowDown size={16} />
                      Down
                    </Button>
                    <Button
                      type="button"
                      onClick={deleteSelectedLessonStep}
                      $danger
                      disabled={saving || lessonSteps.length <= 1}
                    >
                      <Trash2 size={16} />
                      Delete
                    </Button>
                    <Button
                      type="button"
                      onClick={saveSelectedLessonStep}
                      $primary
                      disabled={saving}
                    >
                      <Save size={16} />
                      Save Step to JSON
                    </Button>
                  </ActionRow>
                </StepEditorHeader>

                <StepFormGrid>
                  <Field>
                    <FieldLabel>Step ID</FieldLabel>
                    <Input
                      type="text"
                      value={stepDraft.id}
                      onChange={(event) => updateStepDraft({ id: event.target.value })}
                      placeholder="place-led"
                    />
                  </Field>

                  <Field>
                    <FieldLabel>Title</FieldLabel>
                    <Input
                      type="text"
                      value={stepDraft.title}
                      onChange={(event) => updateStepDraft({ title: event.target.value })}
                      placeholder="Place the LED"
                    />
                  </Field>

                  <FullWidthField>
                    <FieldLabel>Instruction</FieldLabel>
                    <Textarea
                      value={stepDraft.instruction}
                      onChange={(event) => updateStepDraft({ instruction: event.target.value })}
                      placeholder="Tell the learner what to do in this step."
                    />
                  </FullWidthField>

                  <FullWidthField>
                    <FieldLabel>Explanation</FieldLabel>
                    <Textarea
                      value={stepDraft.explanation}
                      onChange={(event) => updateStepDraft({ explanation: event.target.value })}
                      placeholder="Optional background shown with the step."
                    />
                  </FullWidthField>

                  <Field>
                    <FieldLabel>Available components</FieldLabel>
                    <Input
                      type="text"
                      value={stepDraft.availableComponentsText}
                      onChange={(event) => updateStepDraft({ availableComponentsText: event.target.value })}
                      placeholder="led, resistor"
                    />
                    <Hint>Comma-separated. Leave blank to use the lesson-level list.</Hint>
                  </Field>

                  <Field>
                    <FieldLabel>Success message</FieldLabel>
                    <Input
                      type="text"
                      value={stepDraft.successMessage}
                      onChange={(event) => updateStepDraft({ successMessage: event.target.value })}
                      placeholder="Nice work."
                    />
                  </Field>

                  <FullWidthField>
                    <FieldLabel>Allowed actions</FieldLabel>
                    <CheckboxGrid>
                      {LESSON_STEP_ACTION_OPTIONS.map((option) => (
                        <CheckboxLabel key={option.key}>
                          <CheckboxInput
                            type="checkbox"
                            checked={Boolean(stepDraft.allowedActions?.[option.key])}
                            onChange={(event) => updateStepAllowedAction(option.key, event.target.checked)}
                          />
                          {option.label}
                        </CheckboxLabel>
                      ))}
                    </CheckboxGrid>
                  </FullWidthField>

                  <FullWidthField>
                    <FieldLabel>Validation JSON</FieldLabel>
                    <Textarea
                      $compactJson
                      value={stepDraft.validationJson}
                      onChange={(event) => updateStepDraft({ validationJson: event.target.value })}
                      spellCheck={false}
                    />
                  </FullWidthField>

                  <FullWidthField>
                    <FieldLabel>Highlights JSON</FieldLabel>
                    <Textarea
                      $compactJson
                      value={stepDraft.highlightsJson}
                      onChange={(event) => updateStepDraft({ highlightsJson: event.target.value })}
                      spellCheck={false}
                    />
                  </FullWidthField>

                  <FullWidthField>
                    <FieldLabel>Hints JSON</FieldLabel>
                    <Textarea
                      $compactJson
                      value={stepDraft.hintsJson}
                      onChange={(event) => updateStepDraft({ hintsJson: event.target.value })}
                      spellCheck={false}
                    />
                  </FullWidthField>

                  <FullWidthField>
                    <FieldLabel>Correction messages JSON</FieldLabel>
                    <Textarea
                      $compactJson
                      value={stepDraft.correctionMessagesJson}
                      onChange={(event) => updateStepDraft({ correctionMessagesJson: event.target.value })}
                      spellCheck={false}
                    />
                  </FullWidthField>
                </StepFormGrid>

                {stepEditorError ? (
                  <StatusText $tone="error">{stepEditorError}</StatusText>
                ) : null}
              </StepEditorPanel>
            </StepEditorShell>
          ) : (
            <StatusText $tone="error">
              Fix the lesson JSON before editing steps in the form.
            </StatusText>
          )}
        </Field>

        <Field>
          <FieldLabel>Lesson JSON</FieldLabel>
          <Hint>
            The runtime reads this shape directly. Use the sync button after changing metadata fields if you want the JSON view updated before saving.
          </Hint>
          <Textarea
            $json
            value={form.lessonJson}
            onChange={(event) => updateMetadata({ lessonJson: event.target.value })}
            spellCheck={false}
          />
        </Field>

        <Field>
          <FieldLabel>Arduino starter code</FieldLabel>
          <Hint>
            Stored inside the lesson's initial project. Future validation rules can check compile status or code patterns.
          </Hint>
          <Textarea
            $code
            value={form.simulationCode}
            onChange={(event) => updateMetadata({ simulationCode: event.target.value })}
            placeholder="void setup() {\n}\n\nvoid loop() {\n}"
          />
        </Field>

        <ToggleRow>
          <ToggleCopy>
            <ToggleTitle>Published lesson</ToggleTitle>
            <ToggleHint>
              Drafts can be previewed from this browser. Published lessons can be opened and embedded by public URL.
            </ToggleHint>
          </ToggleCopy>

          <ToggleLabel>
            <ToggleInput
              type="checkbox"
              checked={form.isPublished}
              onChange={(event) => updateMetadata({ isPublished: event.target.checked })}
            />
            <ToggleTrack $checked={form.isPublished}>
              <ToggleThumb $checked={form.isPublished} />
            </ToggleTrack>
            <ToggleText>{form.isPublished ? 'Published' : 'Draft'}</ToggleText>
          </ToggleLabel>
        </ToggleRow>

        <ActionRow>
          <Button type="button" onClick={syncMetadataIntoJson} disabled={saving}>
            <FileJson size={16} />
            Sync Metadata
          </Button>

          <Button
            type="button"
            onClick={duplicateCurrentLesson}
            disabled={saving || !parsedLessonSummary.valid}
          >
            <Copy size={16} />
            Duplicate Draft
          </Button>

          <Button
            type="button"
            onClick={handleSave}
            $primary
            disabled={saving || !parsedLessonSummary.valid}
          >
            <Save size={16} />
            {saving ? 'Saving...' : form.recordId ? 'Save Changes' : 'Create Lesson'}
          </Button>

          {form.recordId ? (
            <Button
              type="button"
              onClick={handleDelete}
              $danger
              disabled={saving}
            >
              <Trash2 size={16} />
              Delete
            </Button>
          ) : null}
        </ActionRow>

        <ActionRow>
          <Button
            type="button"
            onClick={() => openLessonPreview(false)}
            disabled={!form.lessonId || !parsedLessonSummary.valid}
          >
            <ExternalLink size={16} />
            Preview Draft
          </Button>

          <Button
            type="button"
            onClick={() => openLessonPreview(true)}
            disabled={!form.lessonId || !parsedLessonSummary.valid}
          >
            <ExternalLink size={16} />
            Preview Embed
          </Button>

          {form.isPublished ? (
            <Button type="button" onClick={() => openPublishedLesson(false)} disabled={!form.lessonId}>
              <ExternalLink size={16} />
              Open Published
            </Button>
          ) : null}

          <Button type="button" onClick={copyIframeUrl} disabled={!form.lessonId || !form.isPublished}>
            <Copy size={16} />
            Copy Iframe URL
          </Button>
        </ActionRow>

        {!parsedLessonSummary.valid ? (
          <StatusText $tone="error">
            Lesson JSON invalid: {parsedLessonSummary.error}
          </StatusText>
        ) : null}
        {error ? <StatusText $tone="error">{error}</StatusText> : null}
        {!error && status ? <StatusText>{status}</StatusText> : null}
      </Card>

      <Card>
        <SectionHeader>
          <div>
            <SectionTitle>
              <BookOpen size={18} />
              Saved Lessons
            </SectionTitle>
            <SectionDescription>
              Select a lesson to edit it, or start a fresh one from the editor.
            </SectionDescription>
          </div>
        </SectionHeader>

        {loading ? (
          <StatusText>Loading lessons...</StatusText>
        ) : lessons.length === 0 ? (
          <EmptyState>
            No Builder lessons have been added yet. Create one here, then publish it when the JSON is ready.
          </EmptyState>
        ) : (
          <List>
            {lessons.map((lesson) => (
              <LessonCard
                key={lesson.recordId}
                type="button"
                $selected={selectedLesson?.recordId === lesson.recordId}
                onClick={() => loadLessonIntoForm(lesson)}
              >
                <LessonTitleRow>
                  <LessonTitle>{lesson.title || lesson.lessonId}</LessonTitle>
                  <LessonBadge $active={lesson.isPublished}>
                    {lesson.isPublished ? <Eye size={12} /> : <EyeOff size={12} />}
                    {lesson.isPublished ? 'Live' : 'Draft'}
                  </LessonBadge>
                </LessonTitleRow>

                <LessonMeta>
                  {lesson.description || lesson.lessonId || 'No description yet.'}
                </LessonMeta>

                <SummaryStrip>
                  <SummaryBadge>
                    <BookOpen size={12} />
                    {lesson.stepCount} steps
                  </SummaryBadge>
                  <SummaryBadge>
                    <CircuitBoard size={12} />
                    {lesson.componentCount} parts
                  </SummaryBadge>
                  <SummaryBadge $tone="code">
                    <Code2 size={12} />
                    {lesson.hasSimulationCode ? 'Code ready' : 'No code'}
                  </SummaryBadge>
                </SummaryStrip>
              </LessonCard>
            ))}
          </List>
        )}
      </Card>
    </Layout>
  );
};

export default ProjectBuilderLessonsManager;
